import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, UploadSimple, Trash, User } from "@phosphor-icons/react";
import { toast } from "react-toastify";
import axiosInstance from "../../utils/axios/axiosConfig";
import StarRating from "./StarRating";
import { Input, Textarea, Select, Button } from "../../components/ui";

const emptyForm = { images: [], comment: "", product_id: "", rating: 0, author: "" };

export default function ReviewModal({ isOpen, onClose, setReviewToUpdate, ReviewToUpdate, setDone, done }) {
  const [loading, setLoading] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const dropRef = useRef(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "unset";
    return () => (document.body.style.overflow = "unset");
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const res = await axiosInstance.post(`/products/search`);
        setAvailableProducts(res?.data?.data || []);
      } catch (e) {
        toast.error(e?.response?.data?.message || "Error fetching products");
      }
    })();

    if (ReviewToUpdate?.id) {
      setFormData({
        images: Array.isArray(ReviewToUpdate.images)
          ? ReviewToUpdate.images
          : (() => {
              try {
                return JSON.parse(ReviewToUpdate.images || "[]");
              } catch {
                return [];
              }
            })(),
        product_id: ReviewToUpdate.product_id || "",
        comment: ReviewToUpdate.comment || "",
        rating: ReviewToUpdate.rating || 0,
        user_id: ReviewToUpdate.user_id || "",
        author: ReviewToUpdate.author || "",
      });
    } else {
      setFormData(emptyForm);
    }
  }, [isOpen, ReviewToUpdate]);

  const onImages = async (files) => {
    const MAX = 6;
    const SIZE_MB = 3;
    const allowed = ["image/jpeg", "image/png", "image/webp"];
    const fileArr = Array.from(files || []);
    if (!fileArr.length) return;
    if (formData.images.length + fileArr.length > MAX) {
      toast.warn(`Maximum ${MAX} images.`);
      return;
    }
    const toBase64 = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    try {
      const valid = fileArr.filter((f) => {
        if (!allowed.includes(f.type)) {
          toast.warn(`Unsupported format: ${f.name}`);
          return false;
        }
        if (f.size > SIZE_MB * 1024 * 1024) {
          toast.warn(`${f.name} exceeds ${SIZE_MB}MB`);
          return false;
        }
        return true;
      });
      const base64Images = await Promise.all(valid.map((f) => toBase64(f)));
      setFormData((prev) => ({ ...prev, images: [...prev.images, ...base64Images] }));
    } catch (e) {
      console.error(e);
      toast.error("Error loading images");
    }
  };

  // drag & drop
  useEffect(() => {
    const zone = dropRef.current;
    if (!zone) return;
    const prevent = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const onDrop = (e) => {
      prevent(e);
      onImages(e.dataTransfer.files);
      zone.classList.remove("border-accent");
    };
    const onDragEnter = (e) => {
      prevent(e);
      zone.classList.add("border-accent");
    };
    const onDragLeave = (e) => {
      prevent(e);
      zone.classList.remove("border-accent");
    };
    zone.addEventListener("drop", onDrop);
    zone.addEventListener("dragenter", onDragEnter);
    zone.addEventListener("dragleave", onDragLeave);
    zone.addEventListener("dragover", prevent);
    return () => {
      zone.removeEventListener("drop", onDrop);
      zone.removeEventListener("dragenter", onDragEnter);
      zone.removeEventListener("dragleave", onDragLeave);
      zone.removeEventListener("dragover", prevent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.images]);

  const removeImage = (index) => setFormData((p) => ({ ...p, images: p.images.filter((_, i) => i !== index) }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const CloseModal = () => {
    setFormData(emptyForm);
    setReviewToUpdate?.(null);
    setDone?.((d) => !d);
    onClose?.();
  };

  const validate = () => {
    if (!formData.product_id) return "Please select a product.";
    if (!formData.rating || formData.rating < 1) return "Please add a rating.";
    if (!formData.author?.trim()) return "Author is required.";
    if (!formData.comment?.trim()) return "Comment is required.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validate();
    if (error) return toast.warn(error);
    setLoading(true);
    try {
      const res = ReviewToUpdate?.id
        ? await axiosInstance.put(`/reviews/update/${ReviewToUpdate.id}`, { ...formData })
        : await axiosInstance.post("/reviews/create", { ...formData });
      toast.success(res?.data?.message || "Saved.");
      CloseModal();
    } catch (err) {
      toast.error(err?.response?.data?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm"
          onClick={CloseModal}
        >
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="flex h-full w-full max-w-md flex-col border-l border-line bg-surface shadow-pop"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-line px-6 py-4">
              <div>
                <h2 className="text-base font-semibold text-ink">
                  {ReviewToUpdate?.id ? "Edit review" : "New review"}
                </h2>
                <p className="text-xs text-muted">Product feedback and rating.</p>
              </div>
              <button onClick={CloseModal} className="grid h-9 w-9 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink" aria-label="Close">
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <form id="review-form" onSubmit={handleSubmit} className="flex-1 space-y-5 overflow-y-auto px-6 py-6 text-start">
              <Select
                label="Product"
                searchable
                placeholder="Select a product"
                options={availableProducts.map((p) => ({ value: p.id, label: p.name }))}
                value={formData.product_id || null}
                onChange={(v) => setFormData((p) => ({ ...p, product_id: v || "" }))}
              />

              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink/90">Rating</label>
                <StarRating rating={formData.rating} onRatingChange={(val) => setFormData((p) => ({ ...p, rating: val }))} />
              </div>

              <Input label="Author" icon={User} name="author" value={formData.author} onChange={handleChange} placeholder="Reviewer name" />
              <Textarea label="Comment" name="comment" value={formData.comment} onChange={handleChange} rows={4} placeholder="What did the customer say?" />

              {/* Images */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink/90">Images</label>
                <label
                  ref={dropRef}
                  htmlFor="review-images"
                  className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-line bg-surface-2 px-6 py-8 text-center transition hover:border-accent/50 hover:bg-surface"
                >
                  <span className="grid h-11 w-11 place-items-center rounded-full border border-line bg-surface text-faint">
                    <UploadSimple size={20} />
                  </span>
                  <span className="text-sm font-medium text-ink">Drag &amp; drop or click to upload</span>
                  <span className="text-xs text-muted">JPG, PNG or WebP · max 3MB · up to 6</span>
                  <input id="review-images" type="file" accept="image/*" multiple className="hidden" onChange={(e) => onImages(e.target.files)} />
                </label>

                {formData.images?.length ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {formData.images.map((src, i) => (
                      <div key={i} className="group relative">
                        <img src={src || "/placeholder.svg"} alt={`Review ${i + 1}`} className="h-20 w-20 rounded-lg border border-line object-cover" />
                        <button
                          type="button"
                          onClick={() => removeImage(i)}
                          className="absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full bg-red-600 text-white shadow transition hover:bg-red-700"
                          aria-label="Remove image"
                        >
                          <Trash size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </form>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 border-t border-line px-6 py-4">
              <Button type="button" variant="outline" onClick={CloseModal}>
                Cancel
              </Button>
              <Button type="submit" form="review-form" loading={loading}>
                {ReviewToUpdate?.id ? "Save changes" : "Create review"}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
